import React, { useState } from 'react';
import { MapPin, CreditCard, BarChart3, Globe, Users, Settings, Shield, Check, ChevronDown, ChevronUp, Building2 } from 'lucide-react';
import type { WizardState } from '../types';
import ReadinessDashboard from '../ReadinessDashboard';

interface Props {
  state: WizardState;
  onEditStep: (step: number) => void;
}

function Section({ title, icon: Icon, step, onEdit, children }: { title: string; icon: any; step: number; onEdit: (s: number) => void; children: React.ReactNode }) {
  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        </div>
        <button type="button" onClick={() => onEdit(step)} className="text-xs text-orange-600 hover:text-orange-700 font-medium">Edit</button>
      </div>
      <div className="p-4 text-sm text-gray-700 space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value || '—'}</span>
    </div>
  );
}

export default function ReviewCreateStep({ state, onEditStep }: Props) {
  const [showReadiness, setShowReadiness] = useState(false);
  const info = state.departmentInfo;
  const addr = info.address;
  const b = state.billing;
  const p = state.inspectionPlan;
  const j = state.departmentInfo?.jurisdiction;
  const auth = state.authenticationSettings;
  const admin = state.initialAdmin;
  const isTrial = b.subscriptionType === 'trial';

  // Get primary contact email/phone from Contacts tab
  const primaryContact = state.contacts?.find(c => c.isPrimary) || state.contacts?.[0];
  const primaryEmail = primaryContact?.emails?.find(e => e.isPrimary)?.address || primaryContact?.emails?.[0]?.address;
  const primaryPhone = primaryContact?.phones?.find(p => p.isPrimary)?.number || primaryContact?.phones?.[0]?.number;

  return (
    <div className="space-y-4">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
        <Check className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-green-900">Ready to create department</p>
          <p className="text-xs text-green-700 mt-0.5">Review the information below and click "Create Department" to provision.</p>
        </div>
      </div>

      <Section title="Department Info" icon={Building2} step={1} onEdit={onEditStep}>
        <Row label="Name" value={info.name} />
        <Row label="Type" value={info.departmentType} />
        <Row label="Classification" value={info.departmentClassification} />
        <Row label="Timezone" value={info.timezone} />
        <Row label="Email" value={info.contactEmail || primaryEmail} />
        <Row label="Phone" value={info.contactPhone || primaryPhone} />
        {addr && <Row label="Address" value={[addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', ')} />}
        {info.nerisId && <Row label="NERIS ID" value={info.nerisId} />}
        <Row
          label="Slug (URL ID)"
          value={info.nerisId ? info.nerisId.toLowerCase() : (info.name ? info.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') : undefined)}
        />
        <div className="flex justify-between py-0.5">
          <span className="text-gray-500">District Boundary</span>
          {info.selectedBoundary ? (
            <span className="font-medium text-green-700">
              ✓ Polygon loaded{info.selectedBoundary.area ? ` (≈${info.selectedBoundary.area.toFixed(1)} sq mi)` : ''}
            </span>
          ) : (
            <span className="font-medium text-amber-600">⚠ No boundary data</span>
          )}
        </div>
        {state.legacySystem.isExistingClient && (
          <Row label="Legacy Link" value={`${state.legacySystem.database} — Dept #${state.legacySystem.departmentId}`} />
        )}
      </Section>

      <Section title="Organization & Tier" icon={Building2} step={2} onEdit={onEditStep}>
        <Row label="Organization" value={state.organizationId || 'Standalone'} />
        <Row label="Tier" value={state.tier} />
      </Section>

      <Section title="Billing" icon={CreditCard} step={4} onEdit={onEditStep}>
        <Row label="Billing Entity" value={b.billingEntity} />
        <Row label="Subscription" value={isTrial ? `Trial (${state.trial?.durationDays || 30} days)` : 'Active'} />
        <Row label="Cycle" value={b.billingCycle} />
        <Row label="Amount" value={b.contractedAmount ? `$${b.contractedAmount.toLocaleString()}` : '$0'} />
        <Row label="Contact" value={b.billingContact?.name} />
      </Section>

      <Section title="Inspection Plan" icon={BarChart3} step={5} onEdit={onEditStep}>
        <Row label="Volume Band" value={p.volumeBand?.toUpperCase()} />
        <Row label="Max Series/Year" value={p.maxSeriesPerYear?.toLocaleString()} />
        <Row label="Commitment" value={p.commitmentTerm} />
        <Row label="Annual Price" value={p.annualPrice ? `$${p.annualPrice.toLocaleString()}` : '$0'} />
        <Row label="Overage" value={p.overagePolicy} />
      </Section>

      {j && (j.country || j.state) && (
        <Section title="Jurisdiction" icon={Globe} step={1} onEdit={onEditStep}>
          <Row label="Country" value={j.country?.name} />
          <Row label="State" value={j.state?.name} />
          <Row label="County" value={j.county?.name} />
          <Row label="City" value={typeof j.city === 'string' ? j.city : (j.city as any)?.name} />
        </Section>
      )}

      <Section title="Contacts" icon={Users} step={3} onEdit={onEditStep}>
        {state.contacts.length === 0 ? (
          <p className="text-gray-400 text-xs">No contacts added</p>
        ) : (
          state.contacts.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <span>{c.name}</span>
              {c.isPrimary && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Primary</span>}
              <span className="text-gray-400">— {c.emails[0]?.address}</span>
            </div>
          ))
        )}
      </Section>

      <Section title="Regulatory Framework" icon={Settings} step={6} onEdit={onEditStep}>
        <Row label="Fire Code" value={state.regulatory?.primaryFireCode ? `${state.regulatory.primaryFireCode} ${state.regulatory.codeEdition || ''}`.trim() : 'Not set'} />
        <Row label="Vendors" value={(state.regulatory?.vendors ?? []).map(v => v.vendorId === 'icc' ? 'ICC Digital Codes' : v.vendorId === 'nfpa' ? 'NFPA LiNK' : 'Custom').join(', ') || 'None'} />
      </Section>

      <Section title="Modules & Settings" icon={Settings} step={7} onEdit={onEditStep}>
        <Row label="Modules" value={state.modules.filter((m) => m.isEnabled).map((m) => m.code).join(', ') || 'None'} />
      </Section>

      <Section title="Admin & Authentication" icon={Shield} step={8} onEdit={onEditStep}>
        <Row label="Auth Method" value={auth.authMethod} />
        <Row label="MFA Required" value={auth.mfa?.required ? 'Yes' : 'No'} />
        <Row label="Admin Idle Timeout" value={`${auth.sessionPolicy?.adminIdleTimeoutMinutes || 60} min`} />
        <Row label="Initial Admin" value={admin.firstName && admin.lastName ? `${admin.firstName} ${admin.lastName} (${admin.email})` : '—'} />
        <Row label="Initial Admin Invite" value={admin.sendInviteImmediately === false ? 'Deferred for later send' : 'Send immediately during provisioning'} />
      </Section>

      {/* Readiness Dashboard — collapsible */}
      <div className="border border-gray-200 rounded-lg">
        <button
          type="button"
          onClick={() => setShowReadiness((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg hover:bg-gray-100 transition-colors"
        >
          <span className="text-sm font-semibold text-gray-900">Readiness Check</span>
          {showReadiness ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>
        {showReadiness && (
          <div className="p-4">
            <ReadinessDashboard state={state} onEditStep={onEditStep} tenantName={info.name} />
          </div>
        )}
      </div>
    </div>
  );
}
